import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type PlatformUser } from './api.js';

/** Platform administrator session. */

interface SessionValue {
  user: PlatformUser | null;
  loading: boolean;
  /** Signed in on a password somebody else issued; the console is unusable until it is replaced. */
  mustChangePassword: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  changePassword: (newPassword: string, currentPassword?: string) => Promise<void>;
  /**
   * Re-reads the signed-in account.
   *
   * The profile screen edits the same user the side nav renders, so without this the name
   * and picture in the corner stay as they were until the next full load — which reads as
   * the save not having worked.
   */
  refreshUser: () => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PlatformUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const load = useCallback(async () => {
    if (!api.isAuthenticated()) {
      setLoading(false);
      return;
    }
    try {
      setUser(await api.get<PlatformUser>('/platform/auth/me'));
    } catch (error) {
      // A reload while on a temporary password: /me is refused like everything else, and treating
      // that as "signed out" would send them back to a sign-in they can only fail forward from.
      if ((error as { code?: string })?.code === 'PASSWORD_CHANGE_REQUIRED') {
        setMustChangePassword(true);
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const onLost = () => setUser(null);
    window.addEventListener('busbook:auth-lost', onLost);
    return () => window.removeEventListener('busbook:auth-lost', onLost);
  }, [load]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.request<{
      user: PlatformUser;
      tokens: { accessToken: string; refreshToken: string };
      mustChangePassword?: boolean;
    }>('/platform/auth/login', {
      method: 'POST',
      auth: false,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    api.setTokens(result.tokens);
    setUser(result.user);
    if (result.mustChangePassword) setMustChangePassword(true);
  }, []);

  const changePassword = useCallback(async (newPassword: string, currentPassword?: string) => {
    const result = await api.post<{
      user: PlatformUser;
      tokens: { accessToken: string; refreshToken: string };
    }>('/platform/auth/change-password', { newPassword, ...(currentPassword ? { currentPassword } : {}) });

    // Setting a password ends every session including this one; the server returns a fresh pair so
    // complying does not cost the user their sign-in.
    api.setTokens(result.tokens);
    setUser(result.user);
    setMustChangePassword(false);
  }, []);

  const refreshUser = useCallback(async () => {
    setUser(await api.get<PlatformUser>('/platform/auth/me'));
  }, []);

  const signOut = useCallback(() => {
    api.setTokens(null);
    setUser(null);
    setMustChangePassword(false);
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, mustChangePassword, signIn, changePassword, refreshUser, signOut }}>
      {children}
    </Ctx.Provider>
  );
}
